import Editor from "~/js/editor";
import type { GraphNode } from "~/js/graph/node-socket-connection";
import type { Coordinates, SizebleEntity } from "~/solidJs/shared/helpers/types/ISizeble";
import type {
	CommentFrame,
	CommentFrameSticker,
	CommentFrameWorkspaceDefinition
} from "../nodes/local/logic/commentFrame";

export const calculateBoundariesOfNode = (node: GraphNode): SizebleEntity => {
	const { x, y } = node.layout.getPosition();

	const width = node.layout.getWidth();
	const height = node.layout.getHeight();

	return {
		left: x,
		right: x + width,
		top: y,
		bottom: y + height,
		width,
		height
	};
};

export const calculateBoundariesOfObject = (
	sticker: CommentFrameSticker | CommentFrame
): SizebleEntity => {
	const { x, y } = sticker.getPosition();

	const { width, height } = sticker.getSize();

	return {
		left: x,
		right: x + width,
		top: y,
		bottom: y + height,
		width,
		height
	};
};

export const calculateBoundariesOfObjectSerialized = (
	sticker: CommentFrameWorkspaceDefinition
): SizebleEntity => {
	const x = sticker.x;
	const y = sticker.y;

	const width = sticker.width;
	const height = sticker.height;

	return {
		left: x,
		right: x + width,
		top: y,
		bottom: y + height,
		width,
		height
	};
};

type CalculateBoundariesOfNodesCtx<NodeType, ObjectType = CommentFrame> = {
	calculateBoundariesOfNode: (node: NodeType) => SizebleEntity;
	calculateBoundariesOfObject: (node: ObjectType) => SizebleEntity;
};

const DefaultCtx: CalculateBoundariesOfNodesCtx<GraphNode, CommentFrame | CommentFrameSticker> = {
	calculateBoundariesOfNode,
	calculateBoundariesOfObject
};

export const calculateBoundariesOfNodes = <NodeType = GraphNode>(
	nodes: NodeType[],
	ctx?: CalculateBoundariesOfNodesCtx<NodeType>
): SizebleEntity => {
	const resolvedCtx = (ctx || DefaultCtx) as CalculateBoundariesOfNodesCtx<any, any>;
	let x = Number.POSITIVE_INFINITY;
	let y = Number.POSITIVE_INFINITY;
	let maxX = Number.NEGATIVE_INFINITY;
	let maxY = Number.NEGATIVE_INFINITY;

	for (const node of nodes) {
		const { left, right, bottom, top } = resolvedCtx.calculateBoundariesOfNode(node);
		if (top < y) y = top;
		if (right > maxX) maxX = right;
		if (left < x) x = left;
		if (bottom > maxY) maxY = bottom;
	}
	return {
		width: maxX - x,
		height: maxY - y,
		left: x,
		top: y,
		right: maxX,
		bottom: maxY
	};
};

export const calculateBoundariesOfObjects = <
	NodeType = GraphNode,
	ObjectType = CommentFrame | CommentFrameSticker
>(
	nodes: NodeType[],
	stickers: Array<ObjectType>,
	ctx?: CalculateBoundariesOfNodesCtx<NodeType, ObjectType>
): SizebleEntity => {
	const resolvedCtx = (ctx || DefaultCtx) as CalculateBoundariesOfNodesCtx<any, any>;
	let x = Number.POSITIVE_INFINITY;
	let y = Number.POSITIVE_INFINITY;
	let maxX = Number.NEGATIVE_INFINITY;
	let maxY = Number.NEGATIVE_INFINITY;

	for (const node of nodes) {
		const { left, right, bottom, top } = resolvedCtx.calculateBoundariesOfNode(node);
		if (top < y) y = top;
		if (right > maxX) maxX = right;
		if (left < x) x = left;
		if (bottom > maxY) maxY = bottom;
	}

	for (const sticker of stickers) {
		const { left, right, bottom, top } = resolvedCtx.calculateBoundariesOfObject(sticker);
		if (top < y) y = top;
		if (right > maxX) maxX = right;
		if (left < x) x = left;
		if (bottom > maxY) maxY = bottom;
	}

	return {
		width: maxX - x,
		height: maxY - y,
		left: x,
		top: y,
		right: maxX,
		bottom: maxY
	};
};

export const moveEditorToBoundary = (
	props: SizebleEntity & {
		scalePadding?: number;
	}
): Coordinates & { zoom: number } => {
	let { width, height, left, top } = props;
	const scalePadding = props.scalePadding ?? 1.5;
	if (height < 500) {
		height = 500;
	}
	const headerPadding = 60;
	const graphWidth = Editor.context.graph.dom.graph.offsetWidth;
	const graphHeight = Editor.context.graph.dom.graph.offsetHeight;
	const graphHeightWithPadding = graphHeight - headerPadding;
	const widthRatio = graphWidth / width;
	const heightRatio = graphHeightWithPadding / height;
	const newCanvasSize = Math.min(widthRatio, heightRatio) / scalePadding;

	const centerHeaderPadding = headerPadding / 2 / newCanvasSize;

	const center = {
		x: graphWidth / 2 - left - width / 2,
		y: graphHeight / 2 + centerHeaderPadding - top - height / 2
	};
	const translateX = center.x * newCanvasSize;
	const translateY = center.y * newCanvasSize;

	return {
		left: translateX,
		top: translateY,
		zoom: newCanvasSize
	};
};

export const fitEditorToBoundary = (
	props: SizebleEntity & {
		scalePadding?: number;
	}
) => {
	const { left, top, zoom } = moveEditorToBoundary(props);
	Editor.context.graph.navigation.setZoomScale(zoom);
	Editor.context.graph.navigation.setPosition(left, top);
};
